import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useGetProfitsQuery, useResetAllProfitsMutation, useResetProviderProfitMutation } from '../../../store/api/profitsApi';

export default function ProfitsPage() {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useGetProfitsQuery();
  const [resetAllDialog, setResetAllDialog] = useState(false);
  const [resetProviderDialog, setResetProviderDialog] = useState<string | null>(null);

  const [resetAllProfits, { isLoading: isResettingAll }] = useResetAllProfitsMutation();
  const [resetProviderProfit, { isLoading: isResettingProvider }] = useResetProviderProfitMutation();

  const profits = data?.data?.profits || {};
  const total = data?.data?.total || 0;

  const providerIds = Object.keys(profits).filter((key) => key !== 'total');

  const handleResetAll = async () => {
    try {
      await resetAllProfits().unwrap();
      toast.success(t('admin.profits.resetAllSuccess') || 'All profits reset successfully');
      setResetAllDialog(false);
      void refetch();
    } catch (err) {
      const error = err as { data?: { error?: string } };
      toast.error(error?.data?.error || t('common.error') || 'Failed to reset profits');
    }
  };

  const handleResetProvider = async (providerId: string) => {
    try {
      await resetProviderProfit(providerId).unwrap();
      toast.success(
        t('admin.profits.resetProviderSuccess', { providerId }) ||
          `Profit for ${providerId} reset successfully`
      );
      setResetProviderDialog(null);
      void refetch();
    } catch (err) {
      const error = err as { data?: { error?: string } };
      toast.error(error?.data?.error || t('common.error') || 'Failed to reset profit');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger">{t('common.error') || 'Error loading profits'}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Reset All Button */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-brand-accent mb-2">
            {t('admin.profits.title') || 'Provider Profits'}
          </h1>
          <p className="text-neutral-medium">
            {t('admin.profits.subtitle') || 'View and manage provider profits'}
          </p>
        </div>
        <button
          onClick={() => setResetAllDialog(true)}
          className="px-6 py-3 bg-danger text-white font-heading font-semibold rounded-lg hover:bg-danger/80 transition-colors duration-200 flex items-center gap-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {t('admin.profits.resetAll') || 'Reset All Profits'}
        </button>
      </div>

      {/* Total Profit Card */}
      <div className="mb-6 bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading font-semibold text-neutral-light mb-2">
              {t('admin.profits.total') || 'Total Profits'}
            </h2>
            <p className="text-3xl font-heading font-bold text-brand-accent">
              ${total.toFixed(2)}
            </p>
          </div>
          <div className="w-16 h-16 rounded-full bg-brand-accent/20 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-brand-accent"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Provider Profits Grid */}
      {providerIds.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {providerIds.map((providerId) => {
            const profit = profits[providerId] || 0;
            return (
              <div
                key={providerId}
                className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50 hover:border-brand-accent/50 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-neutral-light mb-1">
                      {providerId.toUpperCase()}
                    </h3>
                    <p className="text-2xl font-heading font-bold text-brand-accent">
                      ${profit.toFixed(2)}
                    </p>
                  </div>
                  <button
                    onClick={() => setResetProviderDialog(providerId)}
                    className="p-2 text-danger hover:bg-danger/20 rounded-lg transition-colors duration-200"
                    title={t('admin.profits.resetProvider') || 'Reset Provider Profit'}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </button>
                </div>
                <div className="mt-4 pt-4 border-t border-neutral-medium/30">
                  <p className="text-sm text-neutral-medium">
                    {t('admin.profits.providerProfit') || 'Provider Profit'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-12 border border-neutral-dark/50 text-center">
          <p className="text-neutral-medium text-lg">
            {t('admin.profits.noProfits') || 'No profits data available'}
          </p>
        </div>
      )}

      {/* Reset All Dialog */}
      <ConfirmDialog
        isOpen={resetAllDialog}
        onClose={() => setResetAllDialog(false)}
        onConfirm={() => {
          void handleResetAll();
        }}
        title={t('admin.profits.resetAllTitle') || 'Reset All Profits'}
        message={
          t('admin.profits.resetAllConfirm') ||
          'Are you sure you want to reset all provider profits to zero? This action cannot be undone.'
        }
        confirmText={t('common.confirm') || 'Confirm'}
        type="danger"
        isLoading={isResettingAll}
      />

      {/* Reset Provider Dialog */}
      {resetProviderDialog && (
        <ConfirmDialog
          isOpen={!!resetProviderDialog}
          onClose={() => setResetProviderDialog(null)}
          onConfirm={() => {
            if (resetProviderDialog) {
              void handleResetProvider(resetProviderDialog);
            }
          }}
          title={t('admin.profits.resetProviderTitle') || 'Reset Provider Profit'}
          message={
            t('admin.profits.resetProviderConfirm', { providerId: resetProviderDialog }) ||
            `Are you sure you want to reset profit for ${resetProviderDialog} to zero? This action cannot be undone.`
          }
          confirmText={t('common.confirm') || 'Confirm'}
          type="danger"
          isLoading={isResettingProvider}
        />
      )}
    </div>
  );
}

